import { gqlClient } from '@/api/graphql-client';
import { proposalMutationScopeId } from '@/api/mutations/proposals/proposalMutationScope';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import { useMutationState, useQuery } from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';

const GetProposalDocumentQuery = graphql(`
  query GetProposalDocument($input: GetProposalInput!) {
    proposal(input: $input) {
      id
      document {
        html
      }
    }
  }
`);

/**
 * The Proposal rendered as the document the Customer is shown. Only the server
 * can render it, so every edit has to round-trip; `isOutOfSync` is true when the
 * document on screen is behind the edits already made.
 */
export const useGetProposalDocument = (proposalId?: string) => {
  const { data, ...rest } = useQuery({
    enabled: !!proposalId,
    queryKey: proposalKeys.proposalDocument(proposalId!),
    // Every proposal mutation invalidates this key, so freshness does not
    // depend on refetching the expensive server render on mount or refocus.
    staleTime: minutesToMilliseconds(5),
    queryFn: ({ signal }) =>
      gqlClient.request({
        document: GetProposalDocumentQuery,
        variables: { input: { id: proposalId! } },
        signal,
      }),
  });

  const scopeId = proposalId ? proposalMutationScopeId(proposalId) : null;
  const pendingForThisProposal = useMutationState({
    filters: {
      status: 'pending',
      predicate: (m) => scopeId != null && m.options.scope?.id === scopeId,
    },
  });

  return {
    html: data?.proposal.document.html,
    isOutOfSync: pendingForThisProposal.length > 0 || rest.isFetching,
    ...rest,
  };
};

export default useGetProposalDocument;
